import { Link, Typography } from '@material-ui/core';
import {
  FormikCurrencyField,
  FormikDateField,
  FormikNumberField,
  FormikTextField,
} from '@superdispatch/forms';
import { Column, Columns, Stack } from '@superdispatch/ui';
import { TextBox } from '@superdispatch/ui-lab';
import { useFormikContext } from 'formik';
import { MobileAppBridge } from 'shared/data/MobileAppBridge';
import {
  EMPTY_ERROR_MESSAGE,
  FormikMediaField,
} from 'shared/form/FormikMediaField';
import { VerificationStatusMessages } from 'shared/modules/carrier-profile/core/VerificationStatusMessages';
import { useVerifiedCarrierApplicationStatus } from 'shared/modules/carrier-profile/data/VerifiedCarrierApplicationStatusAPI';
import { useCarrierSettings } from 'shared/settings/CarrierSettingsAPI';
import styled from 'styled-components';
import { CargoInsuranceDTO } from '../data/CarrierProfileDTO';
import { ExpediteApprovalBanner } from 'shared/modules/carrier-profile/core/InsuranceBanners';

// Customize styles of calendar icon to make date value fully visible
const StyledFormikDateField = styled(FormikDateField)`
  .MuiInputAdornment-positionEnd {
    margin-left: 0;
    width: 25px;
  }
`;

function InsuranceDeclineReasons() {
  return (
    <Stack>
      <Typography>
        Certificate of Insurance (COI) with Super Dispatch listed as the
        Certificate Holder required. Forward the following certificate holder
        information with your insurance provider:
      </Typography>
      <Typography>
        Super Dispatch <br />
        1321 Upland Dr. PMB 2944
        <br />
        Houston, TX 77043, US
      </Typography>
      <Typography>
        Next steps:
        <br />- Let the insurance provider forward the copy to{' '}
        <Link href="mailto:compliance@superdispatch.com" color="primary">
          compliance@superdispatch.com
        </Link>
        .
        <br />- You are required to upload the new COI in the designated Cargo
        Insurance section of your Carrier Profile.
      </Typography>
    </Stack>
  );
}

function InsuranceCertificateCanceledReasons() {
  return (
    <Stack>
      <Typography>
        Information has been received that the insurance associated with this
        USDOT has been updated/revised. Re-validation is required to ensure
        records remain current.
      </Typography>
      <Typography>
        Next steps:
        <br />- Upload the updated COI (no action needed if the latest COI is
        already on file).
        <br />- Have the insurance provider send a copy of the COI to{' '}
        <Link href="mailto:compliance@superdispatch.com" color="primary">
          compliance@superdispatch.com
        </Link>
        .
      </Typography>
    </Stack>
  );
}

export function ProfileCargoInsuranceForm() {
  const { data } = useVerifiedCarrierApplicationStatus();
  const { data: settings } = useCarrierSettings();
  const formik = useFormikContext<CargoInsuranceDTO>();

  const isEditingDisabled =
    !settings?.drivers_can_edit_carrier_info && MobileAppBridge.isInjected();

  const application = data?.verified_carrier_application;
  const cargoInsuranceReasons = application?.reasons.insurance;

  const externalNotes = application?.external_notes?.cargo_insurance;

  const isInitialCargoInsuranceFile =
    formik.initialValues.file_url === formik.values.file_url;

  const shouldShowError =
    isInitialCargoInsuranceFile && !!cargoInsuranceReasons?.length;
  const hasExpiredInsuranceReason = !!cargoInsuranceReasons?.some(
    (insurance) => insurance.type === 'insurance_certificate_expired',
  );
  const shouldShowExpediteApprovalBanner =
    shouldShowError && hasExpiredInsuranceReason;

  return (
    <Stack space="small">
      <Stack space="small">
        <FormikMediaField
          label="Certificate of Insurance"
          name="file_url"
          fullWidth={true}
          error={shouldShowError}
          disabled={isEditingDisabled}
          validate={() => (shouldShowError ? EMPTY_ERROR_MESSAGE : undefined)}
        />

        {shouldShowError && (
          <VerificationStatusMessages
            title={
              application?.status === 'declined'
                ? 'Certificate of Insurance declined due to:'
                : 'Certificate of Insurance revoked due to:'
            }
            variant="error"
            messages={cargoInsuranceReasons}
            extendedMessages={{
              insurance_not_listed_as_certificate_holder: (
                <InsuranceDeclineReasons />
              ),
              insurance_not_received_by_insurance_agent: (
                <Typography>
                  Let the insurance provider forward the copy of COI to
                  compliance@superdispatch.com and re-submit.
                </Typography>
              ),
              insurance_certificate_canceled: (
                <InsuranceCertificateCanceledReasons />
              ),
            }}
            externalNotes={externalNotes}
          />
        )}

        {shouldShowExpediteApprovalBanner && <ExpediteApprovalBanner />}
      </Stack>

      <Columns space="small" collapseBelow="desktop">
        <Column width={['fluid', '1/3']}>
          <StyledFormikDateField
            format="DateISO"
            label="Expiration Date"
            name="expiration_date"
            fullWidth={true}
            disabled={isEditingDisabled}
          />
        </Column>

        <Column width={['fluid', '1/3']}>
          <FormikNumberField
            label="Cargo Limit"
            name="cargo_limit"
            fullWidth={true}
            disabled={isEditingDisabled}
          />
        </Column>

        <Column width={['fluid', '1/3']}>
          <FormikCurrencyField
            label="Deductible"
            name="deductible_amount"
            fullWidth={true}
            disabled={isEditingDisabled}
          />
        </Column>
      </Columns>

      <Columns space="small" collapseBelow="tablet">
        <Column width={['fluid', '1/2']}>
          <FormikTextField
            label={
              <>
                Auto Policy Number{' '}
                <TextBox color="secondary">(Optional)</TextBox>
              </>
            }
            name="auto_policy_number"
            fullWidth={true}
            disabled={isEditingDisabled}
          />
        </Column>

        <Column width={['fluid', '1/2']}>
          <FormikTextField
            label={
              <>
                Cargo Policy Number{' '}
                <TextBox color="secondary">(Optional)</TextBox>
              </>
            }
            name="cargo_policy_number"
            disabled={isEditingDisabled}
            fullWidth={true}
          />
        </Column>
      </Columns>
    </Stack>
  );
}
